import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from './ForgeButton'
import { Icon } from './Icon'
import { LinkPickerDialog } from './LinkPickerDialog'
import { Mono } from './primitives'
import { cn } from '@/lib/cn'

/**
 * Link work orders, log book entries or anomalies, inside a form.
 *
 * `LinkPickerDialog` wearing a form field's clothes, as `PlaceField` wears
 * `HierarchyPicker`'s: a real label, a button that opens the dialog, the chosen
 * items as removable chips beneath it, and a hint or error in place.
 *
 * The chosen items stay on the form rather than inside a closed control, so
 * what is linked can be re-read before saving. Each carries its serial and title
 * from wherever it was picked — the dialog's page or the record's own summaries
 * — since a later page will not have them.
 *
 * `blocked` disables the button and says why (no place yet, no grant). Items
 * already linked stay listed and removable, because the server re-checks only
 * new links.
 *
 * @param {object} props
 * @param {'workOrders'|'logbookEntries'|'anomalies'} props.kind
 * @param {?object} props.place   As `PlaceField` holds it.
 * @param {?object} props.trio    The same place as `{plantId, lineId, machineId}`.
 * @param {Array<{id: string, serialNumber: string, title: string}>} props.value
 * @param {(items: Array) => void} props.onChange
 * @param {string} [props.label]  Defaults to the kind's plural.
 * @param {boolean} [props.single] One link at most — see `LinkPickerDialog`.
 * @param {number} [props.max]
 * @param {string} [props.blocked] Why it cannot be opened, when it cannot.
 * @param {string} [props.hint]
 * @param {string} [props.error]
 */
export function LinkField({ kind, place, trio, value, onChange, label, single = false, max, blocked, hint, error }) {
  const { t } = useTranslation('app')
  const id = useId()
  const [picking, setPicking] = useState(false)

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-control text-ink">
        {label ?? t(`linkPicker.kinds.${kind}`)}
      </label>

      <ForgeButton
        id={id}
        aria-haspopup="dialog"
        aria-expanded={picking}
        aria-invalid={Boolean(error) || undefined}
        disabled={Boolean(blocked)}
        onClick={() => setPicking(true)}
        className={cn(
          'min-h-11 max-w-none justify-start',
          value.length && 'border-forge/40 text-on-forge',
          error && 'border-red',
        )}
      >
        <Icon name={KIND_ICON[kind]} size="xs" />
        <span className="min-w-0 truncate">
          {single
            ? t(value.length ? `linkPicker.changeOne.${kind}` : `linkPicker.chooseOne.${kind}`)
            : value.length
              ? t(`linkPicker.linked.${kind}`, { count: value.length })
              : t(`linkPicker.choose.${kind}`)}
        </span>
        <Icon name="down" size="xs" className="ms-auto shrink-0" />
      </ForgeButton>

      {value.length ? (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {value.map((item) => (
            <li key={item.id}>
              <ForgeButton
                type="button"
                size="sm"
                onClick={() => onChange(value.filter((one) => one.id !== item.id))}
                aria-label={t('linkPicker.unlink', { id: item.serialNumber })}
                className="max-w-[22rem]"
              >
                <Mono className="font-semibold">{item.serialNumber}</Mono>
                <span className="min-w-0 truncate font-normal">{item.title}</span>
                <Icon name="x" size="xs" className="shrink-0" />
              </ForgeButton>
            </li>
          ))}
        </ul>
      ) : null}

      {error ? (
        <p role="alert" className="m-0 text-body-sm text-on-red">
          {error}
        </p>
      ) : blocked ? (
        <p className="m-0 text-body-sm text-ink-dim">{blocked}</p>
      ) : hint ? (
        <p className="m-0 text-body-sm text-ink-dim">{hint}</p>
      ) : null}

      {picking && place && trio ? (
        <LinkPickerDialog
          kind={kind}
          place={place}
          trio={trio}
          value={value}
          onChange={onChange}
          onClose={() => setPicking(false)}
          single={single}
          max={max}
        />
      ) : null}
    </div>
  )
}

const KIND_ICON = { workOrders: 'wrench', logbookEntries: 'doc', anomalies: 'bolt' }
